import React, { useEffect, useState } from 'react';
import { Box, BoxProps, Button, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Block as BlockIcon, LiveTv as LiveTvIcon } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';

import useCookieSettings from '../../../Hooks/useCookieSettings';
import getVideoProviderFromEmbed from '../../../Utils/getVideoProviderFromEmbed';

const RATIO_16_9 = 56.25;

type StyledContainerProps<T> = T & BoxProps & { rounded: boolean };

const StyledContainer = styled<StyledContainerProps<any>>(Box, {
  shouldForwardProp: (prop: string) => prop !== 'rounded',
})(({ theme: { palette, shape }, rounded }) => ({
  backgroundColor: palette.primary.main,
  color: palette.primary.contrastText,
  position: 'relative',
  paddingBottom: `${RATIO_16_9}%`,
  height: 0,
  textAlign: 'center',
  ...(rounded && { borderRadius: shape.borderRadius * 2 }),
  overflow: 'hidden',
}));

const StyledIframe = styled<StyledContainerProps<any>>('iframe', {
  shouldForwardProp: (prop: string) => prop !== 'size',
})(({ size }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: size,
  height: size,
  border: 0,
}));

function VideoEmbed({ title, src, size = '100%', rounded = false }: any) {
  const { settings, update } = useCookieSettings();
  const srcWithParams = new URL(src);
  const provider = getVideoProviderFromEmbed(src);
  const isCookieApproved = settings[provider];
  const [forcePlay, setForcePlay] = useState(false);
  const [showError, setShowError] = useState(false);

  const enableCookie = () => {
    update({ ...settings, [provider]: true });
    setForcePlay(true);
  };

  useEffect(() => {
    if (isCookieApproved) {
      setTimeout(() => {
        setShowError(true);
      }, 1000);
    }
  }, [showError, isCookieApproved]);

  if (forcePlay) {
    // Works at least for Youtube and Vimeo
    srcWithParams.searchParams.append('autoplay', '1');
  }

  return (
    <StyledContainer rounded={rounded} px={2}>
      <Stack
        spacing={1}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'absolute',
          height: '100%',
          mt: `${RATIO_16_9 / 2}%`,
        }}
      >
        {isCookieApproved ? (
          <>
            <StyledIframe title={title} size={size} src={srcWithParams.toString()} />

            {showError && (
              <>
                <BlockIcon />
                <FormattedMessage id="COOKIES_SETTINGS.VIDEO_PLACEHOLDER.ADS_BLOCK" />
              </>
            )}
          </>
        ) : (
          <>
            <LiveTvIcon />

            <Typography>
              <FormattedMessage
                id={`COOKIES_SETTINGS.VIDEO_PLACEHOLDER.${provider.toUpperCase()}`}
              />
            </Typography>
            <Button variant="outlined" color="inherit" onClick={enableCookie}>
              <FormattedMessage id="COOKIES_SETTINGS.VIDEO_PLACEHOLDER.CTA" />
            </Button>
          </>
        )}
      </Stack>
    </StyledContainer>
  );
}

export default VideoEmbed;
